// dicom-web-api.ts
// DICOMweb standard API client (QIDO-RS, WADO-RS, STOW-RS)
// ref: https://www.dicomstandard.org/using/dicomweb
// ref: https://orthanc.uclouvain.be/book/plugins/dicomweb.html
//
// This module implements the DICOMweb standard over Orthanc's /dicom-web/ plugin.
// For Orthanc's proprietary REST API, see orthanc-api.ts.
//
// DICOMweb uses DICOM UIDs (StudyInstanceUID, SeriesInstanceUID, SOPInstanceUID)
// to address resources — NOT Orthanc's internal short-hash IDs.
//
// Response formats:
//   QIDO-RS  → application/dicom+json (array of DICOM JSON objects)
//   WADO-RS metadata → application/dicom+json
//   WADO-RS retrieve → multipart/related; type="application/dicom" (binary)
//   WADO-RS rendered → image/jpeg or image/png
//   STOW-RS  → multipart/related; type="application/dicom" (upload)

import type { DicomArchiveConfig } from '@/src/supabase';

import z from 'zod';

import { getMessage } from '../utils/exception';
import { orthancJwt } from './useOrthancAuth';

// ================================================================
//  DICOM TAG MAPS
// ================================================================

const tags = {
  PatientID: '00100020',
  PatientName: '00100010',
  PatientBirthDate: '00100030',
  PatientSex: '00100040',

  StudyID: '00200010',
  StudyInstanceUID: '0020000D',
  StudyName: '00100010',
  StudyDate: '00080020',
  StudyTime: '00080030',
  AccessionNumber: '00080050',
  StudyDescription: '00081030',
  Manufacturer: '00080070',
  InstitutionName: '00080080',
  Modality: '00080060',

  WindowCenter: '00281050',
  WindowWidth: '00281051',

  SeriesInstanceUID: '0020000E',
  SeriesNumber: '00200011',
  SeriesDescription: '0008103E',

  ImageComments: '00204000',

  SOPInstanceUID: '00080018',
  SOPClassUID: '00080016',
  InstanceNumber: '00200013',
  NumberOfFrames: '00280008',
  Rows: '00280010',
  Columns: '00280011',
  SamplesPerPixel: '00280002',
  PhotometricInterpretation: '00280004',
  BitsAllocated: '00280100',
  PixelRepresentation: '00280103',
  RescaleIntercept: '00281052',
  RescaleSlope: '00281053',
  RescaleType: '00281054',
  ImageType: '00080008',
  AcquisitionTime: '00080032',
  SequenceName: '00180024',
  SliceThickness: '00180050',
  ProtocolName: '00181030',
  TriggerTime: '00181060',
  CardiacNumberofImages: '00181090',
  FrameofReferenceUID: '00200052',
  SliceLocation: '00201041',
  ImagePositionPatient: '00200032',
  ImageOrientationPatient: '00200037',
  PixelSpacing: '00280030',

  RedPaletteColorLookupTableDescriptor: '00281101',
  GreenPaletteColorLookupTableDescriptor: '00281102',
  BluePaletteColorLookupTableDescriptor: '00281103',
  RedPaletteColorLookupTableDataURI: '00281201',
  GreenPaletteColorLookupTableDataURI: '00281202',
  BluePaletteColorLookupTableDataURI: '00281203',
};

type TagKeys = keyof typeof tags;

const perFrameTags = {
  SharedFunctionalGroupsSequence: '52009229',
  PerFrameFunctionalGroupsSequence: '52009230',

  MREchoSequence: '00189114',
  EchoNumbers: '00180086',
  EffectiveEchoTime: '00189082',

  CardiacTriggerSequence: '00189118',
  TriggerDelayTime: '00209153',
  NominalPercentageOfCardiacPhase: '00209241',
  RRIntervalTimeNominal: '00209251',

  MRAveragesSequence: '00189119',
  NumberOfAverages: '00180083',

  MRImageFrameTypeSequence: '00189226',
  FrameType: '00089007',
  PixelPresentation: '00089205',
  VolumetricProperties: '00089206',
  VolumeBasedCalculationTechnique: '00089207',
  ComplexImageComponent: '00089208',
  AcquisitionContrast: '00089209',

  FrameContentSequence: '00209111',
  FrameAcquisitionDatetime: '00189074',
  FrameReferenceDatetime: '00189151',
  FrameAcquisitionDuration: '00189220',
  StackID: '00209056',
  InStackPositionNumber: '00209057',
  TemporalPositionIndex: '00209128',
  FrameAcquisitionNumber: '00209156',
  DimensionIndexValues: '00209157',

  PlanePositionSequence: '00209113',
  ImagePositionPatient: '00200032',
  PlaneOrientationSequence: '00209116',
  ImageOrientationPatient: '00200037',

  PixelMeasuresSequence: '00289110',
  SliceThickness: '00180050',
  SpacingBetweenSlices: '00180088',
  PixelSpacing: '00280030',
  WindowCenter: '00281050',
  WindowWidth: '00281051',
  PixelValueTransformationSequence: '00289145',
  RescaleIntercept: '00281052',
  RescaleSlope: '00281053',
  RescaleType: '00281054',
  FrameVOILUTSequence: '00289132',

  RedPaletteColorLookupTableDescriptor: '00281101',
  GreenPaletteColorLookupTableDescriptor: '00281102',
  BluePaletteColorLookupTableDescriptor: '00281103',
  RedPaletteColorLookupTableDataURI: '00281201',
  GreenPaletteColorLookupTableDataURI: '00281202',
  BluePaletteColorLookupTableDataURI: '00281203',
};

// ================================================================
//  TYPE DEFINITIONS
// ================================================================

export const ENHANCED_MULTIFRAME_SOP_CLASSES = [
  '1.2.840.10008.5.1.4.1.1.4.1', // Enhanced MR Image Storage
  '1.2.840.10008.5.1.4.1.1.2.1', // Enhanced CT Image Storage
];

export const MREchoItemSchema = z.object({
  EchoNumbers: z.string(),
  EffectiveEchoTime: z.string(),
});
export type MREchoItem = z.infer<typeof MREchoItemSchema>;

export const CardiacTriggerItemSchema = z.object({
  TriggerDelayTime: z.string(),
  NominalPercentageOfCardiacPhase: z.string(),
  RRIntervalTimeNominal: z.string(),
});
export type CardiacTriggerItem = z.infer<typeof CardiacTriggerItemSchema>;

export const MRAveragesItemSchema = z.object({
  NumberOfAverages: z.string(),
});
export type MRAveragesItem = z.infer<typeof MRAveragesItemSchema>;

export const MRImageFrameTypeItemSchema = z.object({
  FrameType: z.string(),
  PixelPresentation: z.string(),
  VolumetricProperties: z.string(),
  VolumeBasedCalculationTechnique: z.string(),
  ComplexImageComponent: z.string(),
  AcquisitionContrast: z.string(),
});
export type MRImageFrameTypeItem = z.infer<typeof MRImageFrameTypeItemSchema>;

export const FrameContentItemSchema = z.object({
  FrameAcquisitionDatetime: z.string(),
  FrameReferenceDatetime: z.string(),
  FrameAcquisitionDuration: z.string(),
  StackID: z.string(),
  InStackPositionNumber: z.string(),
  TemporalPositionIndex: z.string(),
  FrameAcquisitionNumber: z.string(),
  DimensionIndexValues: z.string().array(),
});
export type FrameContentItem = z.infer<typeof FrameContentItemSchema>;

export const PlanePositionItemSchema = z.object({
  ImagePositionPatient: z.string(),
});
export type PlanePositionItem = z.infer<typeof PlanePositionItemSchema>;

export const PlaneOrientationItemSchema = z.object({
  ImageOrientationPatient: z.string(),
});
export type PlaneOrientationItem = z.infer<typeof PlaneOrientationItemSchema>;

export const PixelMeasuresItemSchema = z.object({
  SliceThickness: z.string(),
  SpacingBetweenSlices: z.string(),
  PixelSpacing: z.string(),
});
export type PixelMeasuresItem = z.infer<typeof PixelMeasuresItemSchema>;

export const FrameVOILUTItemSchema = z.object({
  WindowCenter: z.string(),
  WindowWidth: z.string(),
});
export type FrameVOILUTItem = z.infer<typeof FrameVOILUTItemSchema>;

export const PixelValueTransformationItemSchema = z.object({
  RescaleIntercept: z.string(),
  RescaleSlope: z.string(),
  RescaleType: z.string(),
});
export type PixelValueTransformationItem = z.infer<typeof PixelValueTransformationItemSchema>;

export interface PerFrameFunctionalGroupsItem {
  MREchoSequence?: MREchoItem[];
  CardiacTriggerSequence?: CardiacTriggerItem[];
  MRAveragesSequence?: MRAveragesItem[];
  MRImageFrameTypeSequence?: MRImageFrameTypeItem[];
  FrameContentSequence?: FrameContentItem[];
  PlanePositionSequence?: PlanePositionItem[];
  PlaneOrientationSequence?: PlaneOrientationItem[];
  PixelMeasuresSequence?: PixelMeasuresItem[];
  FrameVOILUTSequence?: FrameVOILUTItem[];
  PixelValueTransformationSequence?: PixelValueTransformationItem[];
}

export type Instance = Partial<Record<TagKeys, TagValue>>;

export interface ParsedInstance {
  instance: Instance;
  perFrameGroups?: PerFrameFunctionalGroupsItem[];
  RedPaletteColorLookupTableData?: number[];
  GreenPaletteColorLookupTableData?: number[];
  BluePaletteColorLookupTableData?: number[];
}

export interface FileProgress {
  index: number;
  total: number;
  success: boolean;
  error?: string;
}

// ================================================================
//  DICOM JSON PARSING
// ================================================================

const DicomTagSchema = z.union([
  z.object({
    BulkDataURI: z.string(),
  }),
  z.object({
    Value: z.union([
      z.number().array(),
      z.string().array(),
      // for VR PN
      z.object({
        Alphabetic: z.string(),
      }).array(),
    ]),
  }),
]);

type DicomTag = z.infer<typeof DicomTagSchema>;
type DicomTagValue = Extract<DicomTag, { Value: unknown }>['Value'];
type TagValue = number | number[] | string | string[];

function parseTag(value: unknown): TagValue | undefined {
  function isNumberArray(x: DicomTagValue): x is number[] {
    return typeof x[0] === 'number';
  }
  try {
    const parsedTag = DicomTagSchema.parse(value);
    if ('BulkDataURI' in parsedTag)
      return parsedTag.BulkDataURI;
    if (parsedTag.Value.length === 0) return undefined;
    const values = isNumberArray(parsedTag.Value)
      ? parsedTag.Value
      : parsedTag.Value.map(
          v => typeof v === 'object' ? v.Alphabetic : v,
        );
    if (values.length === 1) return values[0];
    return values;
  }
  catch (error) {
    console.error('Unexpected tag format (%s)', getMessage(error));
    return undefined;
  }
}

function parseInstance(instance: Record<string, unknown>): Instance {
  const withNamedTags = (Object.entries(tags) as [TagKeys, string][])
    .map<[TagKeys, TagValue | undefined]>(([key, tag]) => [key, parseTag(instance[tag])])
    .filter((kv): kv is [TagKeys, TagValue] => kv[1] !== undefined);
  return Object.fromEntries(withNamedTags);
}

function getSeqItems(group: Record<string, unknown> | undefined, seqTag: string) {
  const seq = group?.[seqTag] as { Value: Record<string, unknown>[] } | undefined;
  if (!seq) return undefined;
  return seq.Value;
}

function mapSeq<T extends z.ZodType>(
  schema: T,
  group: Record<string, unknown>,
  seqTag: string,
  mapItem: (item: Record<string, unknown>) => unknown,
): z.infer<T>[] | undefined {
  const items = getSeqItems(group, seqTag);
  if (!items) return undefined;
  return schema.array().parse(items.map(mapItem));
}

function parsePerFrameGroup(
  group: Record<string, unknown>,
): PerFrameFunctionalGroupsItem | undefined {
  return {
    MREchoSequence:
      mapSeq(MREchoItemSchema, group, perFrameTags.MREchoSequence, item => ({
        EchoNumbers: parseTag(item[perFrameTags.EchoNumbers]),
        EffectiveEchoTime: parseTag(item[perFrameTags.EffectiveEchoTime]),
      })),
    CardiacTriggerSequence:
      mapSeq(CardiacTriggerItemSchema, group, perFrameTags.CardiacTriggerSequence, item => ({
        TriggerDelayTime: parseTag(item[perFrameTags.TriggerDelayTime]),
        NominalPercentageOfCardiacPhase: parseTag(
          item[perFrameTags.NominalPercentageOfCardiacPhase],
        ),
        RRIntervalTimeNominal: parseTag(
          item[perFrameTags.RRIntervalTimeNominal],
        ),
      })),
    MRAveragesSequence: mapSeq(
      MRAveragesItemSchema,
      group,
      perFrameTags.MRAveragesSequence,
      item => ({
        NumberOfAverages: parseTag(item[perFrameTags.NumberOfAverages]),
      }),
    ),
    MRImageFrameTypeSequence: mapSeq(
      MRImageFrameTypeItemSchema,
      group,
      perFrameTags.MRImageFrameTypeSequence,
      item => ({
        FrameType: parseTag(item[perFrameTags.FrameType]),
        PixelPresentation: parseTag(item[perFrameTags.PixelPresentation]),
        VolumetricProperties: parseTag(item[perFrameTags.VolumetricProperties]),
        VolumeBasedCalculationTechnique: parseTag(
          item[perFrameTags.VolumeBasedCalculationTechnique],
        ),
        ComplexImageComponent: parseTag(
          item[perFrameTags.ComplexImageComponent],
        ),
        AcquisitionContrast: parseTag(item[perFrameTags.AcquisitionContrast]),
      }),
    ),
    FrameContentSequence: mapSeq(
      FrameContentItemSchema,
      group,
      perFrameTags.FrameContentSequence,
      item => ({
        FrameAcquisitionDatetime: parseTag(
          item[perFrameTags.FrameAcquisitionDatetime],
        ),
        FrameReferenceDatetime: parseTag(
          item[perFrameTags.FrameReferenceDatetime],
        ),
        FrameAcquisitionDuration: parseTag(
          item[perFrameTags.FrameAcquisitionDuration],
        ),
        StackID: parseTag(item[perFrameTags.StackID]),
        InStackPositionNumber: parseTag(
          item[perFrameTags.InStackPositionNumber],
        ),
        TemporalPositionIndex: parseTag(
          item[perFrameTags.TemporalPositionIndex],
        ),
        FrameAcquisitionNumber: parseTag(
          item[perFrameTags.FrameAcquisitionNumber],
        ),
        DimensionIndexValues: parseTag(item[perFrameTags.DimensionIndexValues]),
      }),
    ),
    PlanePositionSequence: mapSeq(
      PlanePositionItemSchema,
      group,
      perFrameTags.PlanePositionSequence,
      item => ({
        ImagePositionPatient: parseTag(item[perFrameTags.ImagePositionPatient]),
      }),
    ),
    PlaneOrientationSequence: mapSeq(
      PlaneOrientationItemSchema,
      group,
      perFrameTags.PlaneOrientationSequence,
      item => ({
        ImageOrientationPatient: parseTag(
          item[perFrameTags.ImageOrientationPatient],
        ),
      }),
    ),
    PixelMeasuresSequence: mapSeq(
      PixelMeasuresItemSchema,
      group,
      perFrameTags.PixelMeasuresSequence,
      item => ({
        SliceThickness: parseTag(item[perFrameTags.SliceThickness]),
        SpacingBetweenSlices: parseTag(item[perFrameTags.SpacingBetweenSlices]),
        PixelSpacing: parseTag(item[perFrameTags.PixelSpacing]),
      }),
    ),
    FrameVOILUTSequence: mapSeq(
      FrameVOILUTItemSchema,
      group,
      perFrameTags.FrameVOILUTSequence,
      item => ({
        WindowCenter: parseTag(item[perFrameTags.WindowCenter]),
        WindowWidth: parseTag(item[perFrameTags.WindowWidth]),
      }),
    ),
    PixelValueTransformationSequence: mapSeq(
      PixelValueTransformationItemSchema,
      group,
      perFrameTags.PixelValueTransformationSequence,
      item => ({
        RescaleIntercept: parseTag(item[perFrameTags.RescaleIntercept]),
        RescaleSlope: parseTag(item[perFrameTags.RescaleSlope]),
        RescaleType: parseTag(item[perFrameTags.RescaleType]),
      }),
    ),
  };
}

export function parsePerFrameData(
  instance: Record<string, unknown>,
): PerFrameFunctionalGroupsItem[] | undefined {
  const seqElem = instance[perFrameTags.PerFrameFunctionalGroupsSequence] as { Value: Record<string, unknown>[] } | undefined;
  const frames = seqElem?.Value;
  if (!frames?.length) return undefined;
  const perFrameGroups = frames
    .map(group => parsePerFrameGroup(group))
    .filter((g): g is PerFrameFunctionalGroupsItem => g !== undefined);
  return perFrameGroups.length ? perFrameGroups : undefined;
}

// ================================================================
//  MULTIPART RESPONSE PARSER
// ================================================================

function extractBoundary(contentType: string): string | null {
  const match = contentType.match(/boundary=([^\s;]+)/i);
  if (!match) return null;
  return match[1].replace(/^"(.*)"$/, '$1');
}

async function parseMultipartResponse(
  response: Response,
): Promise<ArrayBuffer[]> {
  const contentType = response.headers.get('Content-Type') || '';
  const boundary = extractBoundary(contentType);
  if (!boundary) {
    const single = await response.arrayBuffer();
    return single.byteLength > 0 ? [single] : [];
  }

  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const boundaryBytes = new TextEncoder().encode(`--${boundary}`);
  const parts: ArrayBuffer[] = [];

  const findSequence = (
    haystack: Uint8Array,
    needle: Uint8Array,
    start: number,
  ): number => {
    for (let i = start; i <= haystack.length - needle.length; i++) {
      let match = true;
      for (let j = 0; j < needle.length; j++) {
        if (haystack[i + j] !== needle[j]) {
          match = false;
          break;
        }
      }
      if (match) return i;
    }
    return -1;
  };

  let pos = findSequence(bytes, boundaryBytes, 0);
  if (pos === -1) return [];

  while (true) {
    pos += boundaryBytes.length;
    if (
      pos + 2 <= bytes.length
      && bytes[pos] === 0x2D
      && bytes[pos + 1] === 0x2D
    ) {
      break;
    }
    if (pos < bytes.length && bytes[pos] === 0x0D) pos++;
    if (pos < bytes.length && bytes[pos] === 0x0A) pos++;

    let headerEnd = -1;
    for (let i = pos; i < bytes.length - 3; i++) {
      if (
        bytes[i] === 0x0D
        && bytes[i + 1] === 0x0A
        && bytes[i + 2] === 0x0D
        && bytes[i + 3] === 0x0A
      ) {
        headerEnd = i + 4;
        break;
      }
    }
    if (headerEnd === -1) break;

    const nextBoundary = findSequence(bytes, boundaryBytes, headerEnd);
    if (nextBoundary === -1) {
      parts.push(buffer.slice(headerEnd));
      break;
    }

    let dataEnd = nextBoundary;
    if (
      dataEnd >= 2
      && bytes[dataEnd - 2] === 0x0D
      && bytes[dataEnd - 1] === 0x0A
    ) {
      dataEnd -= 2;
    }

    parts.push(buffer.slice(headerEnd, dataEnd));
    pos = nextBoundary;
  }

  return parts;
}

// ================================================================
//  BULK DATA FETCHER
// ================================================================

async function fetchBulkDataOW(
  bulkDataURI: string,
  orthancConfig: DicomArchiveConfig,
): Promise<number[]> {
  try {
    const token = await orthancJwt(orthancConfig.id);
    const response = await fetch(bulkDataURI, {
      headers: {
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        ...(token ? { token } : {}),
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch bulk data:', bulkDataURI, response.status);
      return [];
    }

    const arrayBuffer = await response.arrayBuffer();
    const view = new DataView(arrayBuffer);

    let dataStart = -1;
    for (let i = 0; i < arrayBuffer.byteLength - 3; i++) {
      if (
        view.getUint8(i) === 0x0D
        && view.getUint8(i + 1) === 0x0A
        && view.getUint8(i + 2) === 0x0D
        && view.getUint8(i + 3) === 0x0A
      ) {
        dataStart = i + 4;
        break;
      }
    }

    if (dataStart === -1) {
      console.error('Could not find end of MIME headers');
      return [];
    }

    const headerText = new TextDecoder().decode(
      arrayBuffer.slice(0, dataStart),
    );
    const lengthMatch = headerText.match(/Content-Length:\s*(\d+)/i);
    const contentLength = lengthMatch
      ? Number.parseInt(lengthMatch[1], 10)
      : null;

    const dataEnd = contentLength
      ? dataStart + contentLength
      : arrayBuffer.byteLength;
    const data = arrayBuffer.slice(dataStart, dataEnd);
    return Array.from(new Uint16Array(data));
  }
  catch (error) {
    console.error('Error fetching bulk data:', error);
    return [];
  }
}

// ================================================================
//  FILE HELPERS
// ================================================================

let fileCounter = 0;
function toFile(instance: ArrayBuffer) {
  fileCounter++;
  return new File([new Blob([instance])], `dicom-web.${fileCounter}.dcm`);
}

const NON_DICOM_FILE_NAMES = ['.DS_Store', 'Thumbs.db'];

async function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => resolve(event.target?.result as ArrayBuffer);
    reader.onerror = event =>
      reject(
        new Error(`Error reading file ${file.name}: ${event.target?.error}`),
      );
    reader.readAsArrayBuffer(file);
  });
}

async function readFilesAsArrayBuffers(
  files: FileList,
): Promise<ArrayBuffer[]> {
  return Promise.all(
    Array.from(files)
      .filter(item => !NON_DICOM_FILE_NAMES.includes(item.name))
      .map(async file => readFileAsArrayBuffer(file)),
  );
}

// ================================================================
//  STOW-RS MULTIPART REQUEST BUILDER
// ================================================================

function buildMultipartBody(datasets: ArrayBuffer[]): {
  body: ArrayBuffer;
  contentType: string;
} {
  const boundary = `----DICOMweb-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
  const crlf = '\r\n';
  const encoder = new TextEncoder();

  const parts: ArrayBuffer[] = [];
  datasets.forEach((dataset) => {
    parts.push(
      encoder.encode(
        `--${boundary}${crlf}Content-Type: application/dicom${crlf}${crlf}`,
      ).buffer,
    );
    parts.push(dataset);
    parts.push(encoder.encode(crlf).buffer);
  });
  parts.push(encoder.encode(`--${boundary}--${crlf}`).buffer);

  const totalLength = parts.reduce((sum, p) => sum + p.byteLength, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  parts.forEach((part) => {
    result.set(new Uint8Array(part), offset);
    offset += part.byteLength;
  });

  return {
    body: result.buffer,
    contentType: `multipart/related; type="application/dicom"; boundary=${boundary}`,
  };
}

// ================================================================
//  DICOMWEB CLIENT
// ================================================================

export class DICOMwebClient {
  constructor(private orthancConfig: DicomArchiveConfig) {
    // empty
    // empty
  }

  private async headers(accept?: string): Promise<Record<string, string>> {
    const token = await orthancJwt(this.orthancConfig.id);
    return {
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      ...(token ? { token } : {}),
      ...(accept ? { Accept: accept } : {}),
    };
  }

  private async fetchJson<T>(path: string): Promise<T> {
    const resp = await fetch(`${this.orthancConfig.url}${path}`, {
      headers: await this.headers('application/dicom+json'),
    });
    if (!resp.ok)
      throw new Error(`DICOMweb error [${resp.status}]: GET ${path}`);
    return resp.json() as Promise<T>;
  }

  private async fetchBinary(
    path: string,
    accept = 'image/jpeg',
  ): Promise<ArrayBuffer> {
    const resp = await fetch(`${this.orthancConfig.url}${path}`, {
      headers: await this.headers(accept),
    });
    if (!resp.ok)
      throw new Error(`DICOMweb error [${resp.status}]: GET ${path}`);
    return resp.arrayBuffer();
  }

  private async fetchMultipart(
    path: string,
    accept = 'multipart/related; type="application/octet-stream"',
    compressed = false,
  ): Promise<ArrayBuffer[]> {
    const headers = await this.headers(accept);
    if (compressed) headers['Accept-Encoding'] = 'gzip';
    const resp = await fetch(`${this.orthancConfig.url}${path}`, { headers });
    if (!resp.ok)
      throw new Error(`DICOMweb error [${resp.status}]: GET ${path}`);
    return parseMultipartResponse(resp);
  }

  // ================================================================
  //  QIDO-RS — Query based on ID for DICOM Objects
  //  Standard: PS3.18 §10.6
  // ================================================================

  // DICOMWEB QIDO-RS | GET /studies | Search for studies
  async searchForStudies(queryParams?: Record<string, string>) {
    const qs = queryParams
      ? `?${new URLSearchParams(queryParams).toString()}`
      : '';
    return this.fetchJson<Record<string, unknown>[]>(`/studies${qs}`);
  }

  // DICOMWEB QIDO-RS | GET /series | Search for series (all studies)
  async searchForSeries(queryParams?: Record<string, string>): Promise<any[]> {
    const qs = queryParams
      ? `?${new URLSearchParams(queryParams).toString()}`
      : '';
    return this.fetchJson<any[]>(`/series${qs}`);
  }

  // DICOMWEB QIDO-RS | GET /studies/{study}/series | Search for series in a study
  async searchForStudySeries(
    studyInstanceUID: string,
    queryParams?: Record<string, string>,
  ): Promise<any[]> {
    const qs = queryParams
      ? `?${new URLSearchParams(queryParams).toString()}`
      : '';
    return this.fetchJson<any[]>(`/studies/${studyInstanceUID}/series${qs}`);
  }

  // DICOMWEB QIDO-RS | GET /instances | Search for instances (all studies)
  async searchForInstances(
    queryParams?: Record<string, string>,
  ): Promise<any[]> {
    const qs = queryParams
      ? `?${new URLSearchParams(queryParams).toString()}`
      : '';
    return this.fetchJson<any[]>(`/instances${qs}`);
  }

  // DICOMWEB QIDO-RS | GET /studies/{study}/instances | Search for instances in a study
  async searchForStudyInstances(
    studyInstanceUID: string,
    queryParams?: Record<string, string>,
  ): Promise<any[]> {
    const qs = queryParams
      ? `?${new URLSearchParams(queryParams).toString()}`
      : '';
    return this.fetchJson<any[]>(`/studies/${studyInstanceUID}/instances${qs}`);
  }

  // DICOMWEB QIDO-RS | GET /studies/{study}/series/{series}/instances | Search for instances in a series
  async searchForSeriesInstances(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    queryParams?: Record<string, string>,
  ) {
    const qs = queryParams
      ? `?${new URLSearchParams(queryParams).toString()}`
      : '';
    return this.fetchJson<Record<string, unknown>[]>(
      `/studies/${studyInstanceUID}/series/${seriesInstanceUID}/instances${qs}`,
    );
  }

  // ================================================================
  //  WADO-RS — Web Access to DICOM Objects (Retrieve)
  //  Standard: PS3.18 §10.4
  // ================================================================

  // DICOMWEB WADO-RS | GET /studies/{study}/metadata | Retrieve study metadata
  async retrieveStudyMetadata(studyInstanceUID: string): Promise<any[]> {
    return this.fetchJson<any[]>(`/studies/${studyInstanceUID}/metadata`);
  }

  // DICOMWEB WADO-RS | GET /studies/{study}/series/{series}/metadata | Retrieve series metadata
  async retrieveSeriesMetadata(
    studyInstanceUID: string,
    seriesInstanceUID: string,
  ) {
    return this.fetchJson<Record<string, unknown>[]>(
      `/studies/${studyInstanceUID}/series/${seriesInstanceUID}/metadata`,
    );
  }

  // DICOMWEB WADO-RS | GET .../instances/{instance}/metadata | Retrieve instance metadata
  async retrieveInstanceMetadata(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
  ) {
    return this.fetchJson<Record<string, unknown>[]>(
      `/studies/${studyInstanceUID}/series/${seriesInstanceUID}/instances/${sopInstanceUID}/metadata`,
    );
  }

  // DICOMWEB WADO-RS | GET /studies/{study} | Retrieve entire study
  async retrieveStudy(studyInstanceUID: string): Promise<ArrayBuffer[]> {
    return this.fetchMultipart(
      `/studies/${studyInstanceUID}`,
      'multipart/related; type="application/dicom"',
    );
  }

  // DICOMWEB WADO-RS | GET /studies/{study}/series/{series} | Retrieve entire series
  async retrieveSeries(
    studyInstanceUID: string,
    seriesInstanceUID: string,
  ): Promise<ArrayBuffer[]> {
    return this.fetchMultipart(
      `/studies/${studyInstanceUID}/series/${seriesInstanceUID}`,
      'multipart/related; type="application/dicom"',
    );
  }

  // DICOMWEB WADO-RS | GET .../instances/{instance} | Retrieve single instance
  async retrieveInstance(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
  ): Promise<ArrayBuffer[]> {
    return this.fetchMultipart(
      `/studies/${studyInstanceUID}/series/${seriesInstanceUID}/instances/${sopInstanceUID}`,
      'multipart/related; type="application/dicom"',
    );
  }

  // DICOMWEB WADO-RS | GET .../instances/{instance}/frames/{frames} | Retrieve frames
  async retrieveInstanceFrames(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
    frameNumbers: string,
    compressed = false,
  ): Promise<ArrayBuffer[]> {
    return this.fetchMultipart(
      `/studies/${studyInstanceUID}/series/${seriesInstanceUID}/instances/${sopInstanceUID}/frames/${frameNumbers}`,
      'multipart/related; type="application/octet-stream"',
      compressed,
    );
  }

  // DICOMWEB WADO-RS | GET .../instances/{instance}/rendered | Retrieve instance rendered
  async retrieveInstanceRendered(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
    accept = 'image/jpeg',
  ): Promise<ArrayBuffer> {
    return this.fetchBinary(
      `/studies/${studyInstanceUID}/series/${seriesInstanceUID}/instances/${sopInstanceUID}/rendered`,
      accept,
    );
  }

  // DICOMWEB WADO-RS | GET .../frames/{frames}/rendered | Retrieve frames rendered
  async retrieveInstanceFramesRendered(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
    frameNumbers: string,
    accept = 'image/jpeg',
  ): Promise<ArrayBuffer> {
    return this.fetchBinary(
      `/studies/${studyInstanceUID}/series/${seriesInstanceUID}/instances/${sopInstanceUID}/frames/${frameNumbers}/rendered`,
      accept,
    );
  }

  // DICOMWEB WADO-RS | GET .../instances/{instance}/thumbnail | Retrieve thumbnail
  async retrieveInstanceThumbnail(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
    accept = 'image/jpeg',
  ): Promise<ArrayBuffer> {
    return this.fetchBinary(
      `/studies/${studyInstanceUID}/series/${seriesInstanceUID}/instances/${sopInstanceUID}/thumbnail`,
      accept,
    );
  }

  // DICOMWEB WADO-RS | GET {bulkDataURI} | Retrieve bulk data
  async retrieveBulkData(bulkDataURI: string): Promise<ArrayBuffer> {
    const resp = await fetch(bulkDataURI, {
      headers: await this.headers(
        'multipart/related; type="application/octet-stream"',
      ),
    });
    if (!resp.ok) {
      throw new Error(
        `DICOMweb bulk data error [${resp.status}]: ${bulkDataURI}`,
      );
    }
    return resp.arrayBuffer();
  }

  // ================================================================
  //  STOW-RS — Store Over the Web
  //  Standard: PS3.18 §10.5
  // ================================================================

  // DICOMWEB STOW-RS | POST /studies | Store DICOM instances
  async storeInstances(
    datasets: ArrayBuffer[],
    studyInstanceUID?: string,
  ): Promise<any> {
    const path = studyInstanceUID ? `/studies/${studyInstanceUID}` : '/studies';
    const { body, contentType } = buildMultipartBody(datasets);
    const resp = await fetch(`${this.orthancConfig.url}${path}`, {
      method: 'POST',
      headers: { ...(await this.headers()), 'Content-Type': contentType },
      body,
    });
    if (!resp.ok)
      throw new Error(`STOW-RS error [${resp.status}]: POST ${path}`);
    return resp.json();
  }

  // ================================================================
  //  PARSED CONVENIENCE METHODS
  //  These parse DICOM JSON into typed objects with human-readable tag names.
  // ================================================================

  // QIDO-RS: Search studies, return parsed instances
  async searchStudiesParsed(
    queryParams?: Record<string, string>,
  ): Promise<Instance[]> {
    const raw = await this.searchForStudies(queryParams);
    return raw.map(parseInstance);
  }

  // QIDO-RS: Search series in a study, return parsed instances
  async searchStudySeriesParsed(
    studyInstanceUID: string,
    queryParams?: Record<string, string>,
  ): Promise<Instance[]> {
    const raw = await this.searchForStudySeries(studyInstanceUID, queryParams);
    return raw.map(parseInstance);
  }

  // WADO-RS: Retrieve series metadata, return parsed instances
  async retrieveSeriesMetadataParsed(
    studyInstanceUID: string,
    seriesInstanceUID: string,
  ): Promise<Instance[]> {
    const raw = await this.retrieveSeriesMetadata(
      studyInstanceUID,
      seriesInstanceUID,
    );
    return raw.map(parseInstance);
  }

  // WADO-RS: Retrieve series metadata with per-frame functional groups and palette LUT data
  async retrieveSeriesMetadataEnhanced(
    studyInstanceUID: string,
    seriesInstanceUID: string,
  ): Promise<ParsedInstance[]> {
    const raw = await this.retrieveSeriesMetadata(
      studyInstanceUID,
      seriesInstanceUID,
    );
    const results: ParsedInstance[] = raw.map(instance => ({
      instance: parseInstance(instance),
      perFrameGroups: parsePerFrameData(instance),
    }));

    for (const result of results) {
      const inst = result.instance;
      if (
        typeof inst.RedPaletteColorLookupTableDataURI === 'string'
        && inst.RedPaletteColorLookupTableDataURI.includes('bulk')
      ) {
        result.RedPaletteColorLookupTableData = await fetchBulkDataOW(
          inst.RedPaletteColorLookupTableDataURI,
          this.orthancConfig,
        );
      }
      if (
        typeof inst.GreenPaletteColorLookupTableDataURI === 'string'
        && inst.GreenPaletteColorLookupTableDataURI.includes('bulk')
      ) {
        result.GreenPaletteColorLookupTableData = await fetchBulkDataOW(
          inst.GreenPaletteColorLookupTableDataURI,
          this.orthancConfig,
        );
      }
      if (
        typeof inst.BluePaletteColorLookupTableDataURI === 'string'
        && inst.BluePaletteColorLookupTableDataURI.includes('bulk')
      ) {
        result.BluePaletteColorLookupTableData = await fetchBulkDataOW(
          inst.BluePaletteColorLookupTableDataURI,
          this.orthancConfig,
        );
      }
    }

    return results;
  }

  // WADO-RS: Retrieve instance metadata, return parsed
  async retrieveInstanceMetadataParsed(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
  ): Promise<Instance> {
    const raw = await this.retrieveInstanceMetadata(
      studyInstanceUID,
      seriesInstanceUID,
      sopInstanceUID,
    );
    return parseInstance(raw[0]);
  }

  // WADO-RS: Retrieve instance metadata with per-frame functional groups
  async retrieveInstanceMetadataEnhanced(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
  ) {
    const raw = await this.retrieveInstanceMetadata(
      studyInstanceUID,
      seriesInstanceUID,
      sopInstanceUID,
    );
    return {
      instance: parseInstance(raw[0]),
      perFrameGroups: parsePerFrameData(raw[0]),
    };
  }

  // WADO-RS: Retrieve entire series as File[]
  async retrieveSeriesAsFiles(
    studyInstanceUID: string,
    seriesInstanceUID: string,
  ): Promise<File[]> {
    const parts = await this.retrieveSeries(
      studyInstanceUID,
      seriesInstanceUID,
    );
    return parts.map(toFile);
  }

  // WADO-RS: Retrieve single instance as File
  async retrieveInstanceAsFile(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
  ): Promise<File> {
    const parts = await this.retrieveInstance(
      studyInstanceUID,
      seriesInstanceUID,
      sopInstanceUID,
    );
    return toFile(parts[0]);
  }

  // WADO-RS: Retrieve rendered thumbnail, handling enhanced multiframe "-N" suffix convention
  async retrieveThumbnailUrl(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
  ): Promise<string | null> {
    let frameNumber = sopInstanceUID.split('-')[1];
    if (!frameNumber) frameNumber = '1';
    const cleanSopUID = sopInstanceUID.split('-')[0];

    try {
      const data = await this.retrieveInstanceFramesRendered(
        studyInstanceUID,
        seriesInstanceUID,
        cleanSopUID,
        frameNumber,
        'image/jpeg',
      );
      if (!data || data.byteLength === 0) return null;
      return URL.createObjectURL(new Blob([data], { type: 'image/jpeg' }));
    }
    catch {
      return null;
    }
  }

  // WADO-RS: Retrieve raw 16-bit frame data, handling "-N" suffix convention
  async retrieveFrameRaw16(
    studyInstanceUID: string,
    seriesInstanceUID: string,
    sopInstanceUID: string,
  ): Promise<Uint16Array | undefined> {
    const [cleanSopUID, framePart] = sopInstanceUID.split('-');
    const frameNumbers = framePart || '0';
    const parts = await this.retrieveInstanceFrames(
      studyInstanceUID,
      seriesInstanceUID,
      cleanSopUID,
      frameNumbers,
    );
    const buf = parts?.[0];
    return buf ? new Uint16Array(buf) : undefined;
  }

  // STOW-RS: Upload DICOM files from FileList with per-file progress
  async storeFromFileList(
    dicomFiles: FileList,
    progressCallback?: (progress: FileProgress) => void,
  ) {
    const arrayBuffers = await readFilesAsArrayBuffers(dicomFiles);
    for (let i = 0; i < arrayBuffers.length; i++) {
      try {
        await this.storeInstances([arrayBuffers[i]]);
        progressCallback?.({
          index: i + 1,
          total: arrayBuffers.length,
          success: true,
        });
      }
      catch (error) {
        console.error('Error storing DICOM instance:', error);
        progressCallback?.({
          index: i + 1,
          total: arrayBuffers.length,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }
  }
}

// ================================================================
//  FACTORY
// ================================================================

export function createDICOMwebClient(
  orthancConfig: DicomArchiveConfig,
): DICOMwebClient {
  return new DICOMwebClient(orthancConfig);
}

// ================================================================
//  URL PARSER
// ================================================================

const LEVELS = ['series', 'studies'] as const;
type DicomLevel = (typeof LEVELS)[number];

export function parseUrl(deepDicomWebUrl: string) {
  const sansSlash = deepDicomWebUrl.replace(/\/$/, '');
  let paths = sansSlash.split('/');

  const parentIDs = LEVELS.reduce(
    (idObj, dicomLevel) => {
      const [urlLevel, dicomID] = paths.slice(-2);
      if (urlLevel === dicomLevel) {
        paths = paths.slice(0, -2);
        return { [dicomLevel]: dicomID, ...idObj };
      }
      return idObj;
    },
    {} as Partial<Record<DicomLevel, string>>,
  );

  const pathsToSlice = Object.keys(parentIDs).length * 2;
  const allPaths = sansSlash.split('/');
  const host = allPaths.slice(0, allPaths.length - pathsToSlice).join('/');

  return { host, ...parentIDs };
}

export default createDICOMwebClient;
